import FVector from '@data/geometry/FVector';
import { ColorModeOptions } from '@gui/constants/ColorModeOptions';
import { Panels } from '@gui/constants/Panels';
import { ThemeMode } from '@gui/theme';
import type {
    AmbientState,
    Camera,
    ContextState,
    DataState,
    DeepPartial,
    OverlaysState,
    SettingsState,
    SolarEnergyResourcesState,
    GeodataState,
} from '@visualizer/types';
import { CameraMode } from '@visualizer/types';

const getDefaultThemeMode = (): ThemeMode => {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
};
const DEFAULT_THEME_MODE: ThemeMode = getDefaultThemeMode();

export type GlobalState = {
    [Panels.CONTEXT]: ContextState;
    [Panels.AMBIENT]: AmbientState;
    [Panels.SOLAR_ENERGY_RESOURCES]: SolarEnergyResourcesState;
    [Panels.SETTINGS]: SettingsState;
    [Panels.DATA]: DataState;
    [Panels.GEODATA]: GeodataState;
    [Panels.OVERLAYS]: OverlaysState;
};

export type LayerSlice = 'data' | 'projection' | 'element' | 'simulationItems' | 'segments' | 'filter';
export type ResetTarget =
    | { scope: 'store' }
    | { scope: 'global' }
    | { scope: 'globalPanel'; panel: GlobalPanel }
    | { scope: 'layer'; layerId?: string }
    | { scope: 'layerPanel'; slice: LayerSlice; layerId?: string };

export type GlobalPanel = keyof GlobalState;

export type GlobalActions = {
    setPanel: <P extends GlobalPanel>(updates: DeepPartial<GlobalState[P]>, panel: P) => void;
    // setPanel: <P extends Panels>(updates: Partial<GlobalState[P]>, panel: P) => void;

    reset: (target: ResetTarget) => void;

    setCamera: (camera: Camera) => void;
};

export const createGlobalState = (): GlobalState => {
    return {
        context: {
            showGround: true,
            showBuildings: true,
            showTrees: true,
            showRoads: true,
            showTargets: true,
            showWindStations: false,
            showCustomObstruction: true,
            showProjectSketch: true,

            showGrid: true,
            showSiteBox: false,
            showAnalysisVolume: false,
            showGridCenter: false,
            showAnalysisBorder: true,
            showLegalBorder: true,
            showBuildableBorder: false,
            showGridAxes: false,
        },
        ambient: {
            mode: 'standard',
            showStrokes: true,
            wasdEnabled: true,
            cameraWasdSpeed: 3,
            currentCamera: {
                type: CameraMode.STANDARD,
                target: new FVector(),
                position: new FVector(),
            },
        },
        solarEnergyResources: {
            solarParams: {
                diffuseRadiation: { on: true, value: 80, minValue: 50, maxValue: 100 },
                cloudCoverage: { on: true, value: 60, minValue: 0, maxValue: 100 },
                unitOfMeasure: 0,
            },
            buildingPatterns: {
                grossFloorSurface: { value: -1, minValue: 0, maxValue: 1000000 },
                use: 'Residential',
            },
            energyDemand: {
                yearlyRateMode: 'default',
                yearlyRateManual: 72,
                totalPeakMonth: 'Jan',
                totalPeakRate: 72,
                heatingPeakMonth: 'Jan',
                heatingPeakRate: 72,
            },
            demandPeaks: {
                heatingPeaks: {
                    occurrences: ['Jan', '-'],
                    type: ['Monthly', 'Hourly'],
                    values: ['1.9', '-'],
                },
                coolingPeaks: {
                    occurrences: ['Jul', '18'],
                    type: ['Monthly', 'Hourly'],
                    values: ['0.4', '10'],
                },
                appliancesPeaks: {
                    occurrences: ['-', '7', '18'],
                    type: ['Monthly', 'Hourly', ''],
                    values: ['-', '27.40', '44.75'],
                },
                totalPeaks: {
                    occurrences: [],
                    type: [],
                    values: [],
                },
            },
            photovoltaicPotentials: {
                strategy: 'Manual',
                panelEfficiency: { minValue: 10, maxValue: 40, value: 20 },
                surfaceCoverage: { minValue: 0, maxValue: 100, value: 100 },
                totalEnergySupply: 0,
                demand: 0,
                blockIdsByLayer: {},
            },
            solarHeatGains: {
                strategy: 'Manual',
                gValue: { minValue: 25, value: 60, maxValue: 85 },
                transmittedEnergy: 0,
                heatingPeaksDemand: 0,
                blockIdsByLayer: {},
            },
            solarMatching: {
                currentMatchingDemand: null, // start with no active job
                matchingQueue: [], // empty queue of jobs
                isMatchingDemand: false, // not processing yet
            },
            yearlyDemand: 0,
        },
        settings: {
            colorScheme: ColorModeOptions.LightGreyBlueGreen,
            coloredItems: true,
            themeMode: DEFAULT_THEME_MODE,
            lowPerformance: false,
            coordinateSystem: {
                mode: 'lv95',
                centerX: 0,
                centerY: 0,
                centerZ: 0,
                alpha: 0,
            },
        },
        data: {
            isMixerMode: false,
            mixerAndLogic: false,
            mixerHigherLogic: false,
            multimode: true,
            showSelected: true,
        },
        geodata: {
            editGeodataEnabled: false,
        },
        overlays: {
            legendVisible: true,
            fpsIndicatorVisible: true,
            compassVisible: true,
            absoluteMinMax: [0, 1],
            remappedMinMax: [0, 1],
            tmpMinMax: [0, 1],
        },
    };
};
